/**
 * usePokerWebSocket — real-time hook for live poker table state
 *
 * Connect flow:
 *  1. Open /ws connection
 *  2. Send { type: 'authenticate', token } — token issued by REST join endpoint
 *  3. On 'authenticated', send { type: 'join_game', gameId } to subscribe
 *  4. Merge incoming game_state_update / hand_complete events into local state
 *
 * Falls back gracefully to REST polling when WS is unavailable.
 */
import { useEffect, useRef, useCallback, useState } from 'react';
import type { GameState } from '../../../server/services/poker-game-state-manager';
import { wsUrl } from '../lib/api';

export interface WSGameState extends GameState {
  tableId?: string;
}

export interface LastAction {
  playerId: string;
  action: string;
  amount?: number;
}

export interface HandCompleteEvent {
  winnerId: string;
  winnerName: string;
  handRank: string;
  potAmount: number;
  isTie?: boolean;
  tiedPlayers?: string[];
  playerChipDeltas?: Record<string, number>;
}

export interface BlindLevelEvent {
  level: number;
  sb: number;
  bb: number;
}

/** Shared table chat from WS broadcast */
export interface ChatMessageEvent {
  id: string;
  playerId: string;
  text: string;
  at: number;
}

interface UsePokerWebSocketOptions {
  tableId: string;
  /** Single-use auth token issued by POST /api/tables/:id/join response. */
  wsToken?: string;
  onHandComplete?: (event: HandCompleteEvent) => void;
  onBlindLevelUp?: (event: BlindLevelEvent) => void;
  onError?: (msg: string) => void;
}

interface UsePokerWebSocketReturn {
  gameState: WSGameState | null;
  lastAction: LastAction | null;
  chatMessages: ChatMessageEvent[];
  connected: boolean;
  authenticated: boolean;
  sendAction: (action: string, amount?: number) => void;
  sendChat: (text: string) => void;
}

const WS_RECONNECT_DELAY = 4000;
const MAX_RECONNECT_ATTEMPTS = 8;

export function usePokerWebSocket({
  tableId,
  wsToken,
  onHandComplete,
  onBlindLevelUp,
  onError,
}: UsePokerWebSocketOptions): UsePokerWebSocketReturn {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmounted = useRef(false);
  // Keep latest wsToken accessible inside stable callbacks without re-creating them
  const wsTokenRef = useRef(wsToken);
  useEffect(() => { wsTokenRef.current = wsToken; }, [wsToken]);

  const [gameState, setGameState] = useState<WSGameState | null>(null);
  const [lastAction, setLastAction] = useState<LastAction | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessageEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);

  const send = useCallback((msg: object) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  // Stable message handler — uses refs for external callbacks to avoid stale closures
  const onHandCompleteRef = useRef(onHandComplete);
  const onBlindLevelUpRef = useRef(onBlindLevelUp);
  const onErrorRef = useRef(onError);
  useEffect(() => { onHandCompleteRef.current = onHandComplete; }, [onHandComplete]);
  useEffect(() => { onBlindLevelUpRef.current = onBlindLevelUp; }, [onBlindLevelUp]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  const handleMessage = useCallback((msg: any) => {
    switch (msg.type) {
      case 'connected':
        // Server acknowledged connection — nothing more to do here
        break;

      case 'authenticated':
        setAuthenticated(true);
        // Store the rotated token so future reconnects can re-authenticate
        if (msg.nextToken) {
          wsTokenRef.current = msg.nextToken;
          // Persist to sessionStorage for the table page (key matches table.tsx convention)
          try { sessionStorage.setItem(`ws_token_${tableId}`, msg.nextToken); } catch (_) {}
        }
        // Subscribe to the table room (player is already seated via REST)
        send({ type: 'join_game', gameId: tableId });
        break;

      case 'auth_error':
        onErrorRef.current?.(`WS auth error: ${msg.message}`);
        break;

      case 'join_error':
        // Not seated yet — just request the public game state as an observer
        send({ type: 'get_game_state', gameId: tableId });
        break;

      case 'game_state':
      case 'game_state_update': {
        const gs: WSGameState = msg.gameState ?? msg;
        if (gs) {
          setGameState(prev => ({ ...(prev ?? {}), ...gs, tableId }));
          if (msg.lastAction) setLastAction(msg.lastAction);
        }
        break;
      }

      case 'player_action':
        if (msg.gameState) setGameState(prev => ({ ...(prev ?? {}), ...msg.gameState, tableId }));
        if (msg.action) setLastAction({ playerId: msg.userId ?? msg.playerId, action: msg.action, amount: msg.amount });
        break;

      case 'hand_complete':
        if (msg.gameState) setGameState(prev => ({ ...(prev ?? {}), ...msg.gameState, tableId }));
        if (msg.result) onHandCompleteRef.current?.(msg.result);
        break;

      case 'blind_level_up':
        onBlindLevelUpRef.current?.(msg);
        break;

      case 'player_joined':
      case 'player_left':
        // Refresh game state
        send({ type: 'get_game_state', gameId: tableId });
        break;

      case 'chat_message': {
        const playerId = String(msg.playerId ?? msg.wallet ?? 'player');
        const text = String(msg.text ?? msg.message ?? '').slice(0, 400);
        if (!text) break;
        const id = String(
          msg.id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        );
        setChatMessages((prev) => {
          if (prev.some((m) => m.id === id)) return prev;
          return [
            ...prev,
            {
              id,
              playerId,
              text,
              at: Number(msg.at) || Date.now(),
            },
          ].slice(-80);
        });
        break;
      }

      case 'error':
        onErrorRef.current?.(msg.message);
        break;
    }
  }, [tableId, send]); // stable — all callbacks via refs

  const connect = useCallback(() => {
    if (unmounted.current) return;
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) return;

    const url = wsUrl(wsTokenRef.current ?? undefined, tableId);
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (unmounted.current) { ws.close(); return; }
      reconnectAttempts.current = 0;
      setConnected(true);
      // Authenticate immediately if we have a token
      const tok = wsTokenRef.current;
      if (tok) ws.send(JSON.stringify({ type: 'authenticate', token: tok }));
    };

    ws.onmessage = (event) => {
      try { handleMessage(JSON.parse(event.data)); } catch (_) {}
    };

    ws.onclose = () => {
      setConnected(false);
      setAuthenticated(false);
      wsRef.current = null;
      if (!unmounted.current && reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttempts.current++;
        reconnectTimer.current = setTimeout(connect, WS_RECONNECT_DELAY);
      }
    };

    ws.onerror = () => {
      onErrorRef.current?.('WebSocket connection error');
    };
  }, [handleMessage]);

  useEffect(() => {
    unmounted.current = false;
    connect();
    return () => {
      unmounted.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [connect]);

  // When a token arrives after the socket is already open (late join), authenticate now
  useEffect(() => {
    if (connected && wsToken && !authenticated) {
      send({ type: 'authenticate', token: wsToken });
    }
  }, [connected, wsToken, authenticated, send]);

  const sendAction = useCallback((action: string, amount?: number) => {
    send({ type: 'player_action', action, amount });
  }, [send]);

  const sendChat = useCallback((text: string) => {
    send({ type: 'chat_message', text });
  }, [send]);

  const sendSitOut = useCallback((sittingOut: boolean) => {
    send({ type: sittingOut ? 'sit_out' : 'sit_in' });
  }, [send]);

  return {
    gameState,
    lastAction,
    chatMessages,
    connected,
    authenticated,
    sendAction,
    sendChat,
    sendSitOut,
  };
}
